import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import {
  isQuickPeriodValue,
  presetMinutes,
  quickPeriodRange,
} from '@sensika/ag-grid-kit';

import { Period } from 'src/app/core/models/core.models';

/** The window a now-relative preset stands for at `nowMs`, in unix seconds. */
export function relativePeriod(minutes: number, nowMs = Date.now()): Period {
  const endTimestamp = Math.floor(nowMs / 1000);
  return { startTimestamp: endTimestamp - minutes * 60, endTimestamp };
}

/**
 * The window any preset stands for at `nowMs`: an hour preset slides with now,
 * a calendar preset (3D, 1W, ..., today) re-anchors to the current day; null
 * for an unknown name. Every preset is relative unless the period is locked,
 * which freezes the resolved range and drops the preset (WAP-1951).
 */
export function presetPeriod(
  preset: string | null | undefined,
  nowMs = Date.now(),
): Period | null {
  const minutes = presetMinutes(preset);
  if (minutes !== null) {
    return relativePeriod(minutes, nowMs);
  }
  if (preset && isQuickPeriodValue(preset)) {
    const { from, to } = quickPeriodRange(preset, new Date(nowMs));
    return {
      startTimestamp: Math.floor(from.getTime() / 1000),
      endTimestamp: Math.floor(to.getTime() / 1000),
    };
  }
  return null;
}

/**
 * "Last 48 hours" / "Last 15 minutes" for a now-relative duration: hours when
 * the minutes divide into whole hours, minutes otherwise - the time row's own
 * units, never days (owner, WAP-1950).
 */
export function relativePeriodLabel(
  minutes: number,
  translateService: TranslateService,
): string {
  const [count, unit] =
    minutes % 60 === 0 ? [minutes / 60, 'hour'] : [minutes, 'minute'];
  const t = (key: string): string =>
    translateService.instant(`date-range-picker.${key}`);
  return `${t('relative.last')} ${count} ${t(count === 1 ? `unit.${unit}` : `unit.${unit}s`)}`;
}

/**
 * The period as a title suffix: the relative label while a now-relative
 * preset is active, else the absolute range in `dateFormat`.
 */
export function periodLabel(
  period: Period,
  preset: string | null | undefined,
  translateService: TranslateService,
  dateFormat = 'dd MMM HH:mm',
): string {
  const minutes = presetMinutes(preset);
  return minutes === null
    ? `${format(period.startTimestamp * 1000, dateFormat)} - ${format(period.endTimestamp * 1000, dateFormat)}`
    : relativePeriodLabel(minutes, translateService);
}
